import type { InfoHtmlStyle } from "./InfoHtmlNode";

/**
 * Parses the `style` attribute string of an HTML element into a typed
 * `InfoHtmlStyle` object.
 *
 * Supported declarations (CSS property → typed field):
 * - `padding` / `padding-{top,right,bottom,left}` → `paddingTop` etc. (px)
 * - `margin`  / `margin-{top,right,bottom,left}`  → `marginTop` etc. (px)
 * - `width` / `height` → px
 * - `text-align: left | center | right`
 * - `color: #rgb | #rrggbb`
 * - `background-color: #rgb | #rrggbb`
 * - `font-size: <N>px`
 * - `font-weight: normal | bold | <numeric>` (numeric: ≥600 → bold, else normal)
 *
 * Unknown properties and malformed values are silently dropped. Px-only —
 * `em` / `rem` / `%` / unitless values for length properties are dropped.
 *
 * Shorthand `padding: 8px 12px 4px 16px` expands per CSS rules:
 * - 1 value: all sides
 * - 2 values: top/bottom, left/right
 * - 3 values: top, left/right, bottom
 * - 4 values: top, right, bottom, left
 */
export function parseInlineStyle(styleAttr: string | null | undefined): InfoHtmlStyle | undefined {
  if (!styleAttr) return undefined;

  const out: InfoHtmlStyle = {};
  const declarations = styleAttr.split(";");

  for (const decl of declarations) {
    const colonIndex = decl.indexOf(":");
    if (colonIndex < 0) continue;

    const property = decl.slice(0, colonIndex).trim().toLowerCase();
    const value = decl.slice(colonIndex + 1).trim();
    if (property.length === 0 || value.length === 0) continue;

    applyDeclaration(out, property, value);
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

function applyDeclaration(target: InfoHtmlStyle, property: string, value: string): void {
  switch (property) {
    case "padding":     applyBoxShorthand(target, value, "padding"); return;
    case "padding-top":    setPxIfValid(target, "paddingTop", value); return;
    case "padding-right":  setPxIfValid(target, "paddingRight", value); return;
    case "padding-bottom": setPxIfValid(target, "paddingBottom", value); return;
    case "padding-left":   setPxIfValid(target, "paddingLeft", value); return;

    case "margin":      applyBoxShorthand(target, value, "margin"); return;
    case "margin-top":     setPxIfValid(target, "marginTop", value); return;
    case "margin-right":   setPxIfValid(target, "marginRight", value); return;
    case "margin-bottom":  setPxIfValid(target, "marginBottom", value); return;
    case "margin-left":    setPxIfValid(target, "marginLeft", value); return;

    case "width":  setPxIfValid(target, "width",  value); return;
    case "height": setPxIfValid(target, "height", value); return;

    case "text-align":
      if (value === "left" || value === "center" || value === "right") {
        target.textAlign = value;
      }
      return;

    case "color":
      if (isValidHexColor(value)) target.color = value;
      return;

    case "background-color":
      if (isValidHexColor(value)) target.backgroundColor = value;
      return;

    case "font-size": {
      const px = tryParsePx(value);
      if (px !== null) target.fontSize = px;
      return;
    }

    case "font-weight":
      target.fontWeight = parseFontWeight(value);
      return;
  }
}

type BoxSide = "Top" | "Right" | "Bottom" | "Left";
const boxOrder4: BoxSide[] = ["Top", "Right", "Bottom", "Left"];

function applyBoxShorthand(
  target: InfoHtmlStyle,
  value: string,
  prefix: "padding" | "margin",
): void {
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length === 0 || parts.length > 4) return;

  const sides: Record<BoxSide, string> =
    parts.length === 1 ? { Top: parts[0], Right: parts[0], Bottom: parts[0], Left: parts[0] }
    : parts.length === 2 ? { Top: parts[0], Right: parts[1], Bottom: parts[0], Left: parts[1] }
    : parts.length === 3 ? { Top: parts[0], Right: parts[1], Bottom: parts[2], Left: parts[1] }
    : { Top: parts[0], Right: parts[1], Bottom: parts[2], Left: parts[3] };

  for (const side of boxOrder4) {
    const px = tryParsePx(sides[side]);
    if (px === null) continue;
    const key = (prefix + side) as keyof InfoHtmlStyle;
    (target as Record<keyof InfoHtmlStyle, unknown>)[key] = px;
  }
}

function setPxIfValid(target: InfoHtmlStyle, key: keyof InfoHtmlStyle, value: string): void {
  const px = tryParsePx(value);
  if (px === null) return;
  (target as Record<keyof InfoHtmlStyle, unknown>)[key] = px;
}

/**
 * Parses a px length value. Returns the number (may be 0 or negative — caller
 * decides what's meaningful). Returns null for unit-less, em/rem/%, or
 * unparseable input.
 */
function tryParsePx(value: string): number | null {
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "0") return 0;
  if (!trimmed.endsWith("px")) return null;
  const num = Number(trimmed.slice(0, -2).trim());
  return Number.isFinite(num) ? num : null;
}

const hex3 = /^#[0-9a-f]{3}$/i;
const hex6 = /^#[0-9a-f]{6}$/i;
function isValidHexColor(value: string): boolean {
  return hex3.test(value) || hex6.test(value);
}

function parseFontWeight(value: string): "bold" | "normal" {
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "bold") return "bold";
  if (trimmed === "normal") return "normal";
  const num = Number(trimmed);
  if (Number.isFinite(num) && num >= 600) return "bold";
  return "normal";
}
